// ────────────────────────────────────────────────────────────────
// src/pages/MyPayoutsPage.js
//
// 2025-07-19 • Full-length version (nothing omitted)
//
//   • Four-box grid:  (1) Available for Payout, (2) Payment Day,
//                     (3) Available Soon, (4) Max Payout Date.
//   • “PayMe Now” button shows ONLY if user has >0 available JPY
//     and schedule = manual.
//   • Modal on success / error.
// ────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

// custom axios with interceptors
import axiosInstance from '../utils/axiosInstance'

function MyPayoutsPage() {
  const { lang } = useOutletContext() || {}

  /* ─────────── State ─────────── */
  const [interval,  setIntervalVal] = useState('')
  const [anchor,    setAnchor]      = useState('')
  const [balance,   setBalance]     = useState(0)   // pending
  const [available, setAvailable]   = useState(0)   // payout-ready
  const [expectedDate, setExpectedDate] = useState(null)

  /* instant-payout UX */
  const [isPayingNow, setIsPayingNow] = useState(false)
  const [modalOpen,   setModalOpen]   = useState(false)
  const [modalMsg,    setModalMsg]    = useState('')
  const [modalOk,     setModalOk]     = useState(true)

  /* loading / error */
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')

  const token = localStorage.getItem('token')
  const user  = JSON.parse(localStorage.getItem('user') || '{}')

  /* ─────────── i18n ─────────── */
  const translations = {
    en: {
      pageTitle         : 'My Payouts',
      paymentInterval   : 'Available for Payout',
      paymentDay        : 'Payment Day',
      payoutBalanceSoon : 'Available Soon',
      expectedDateLabel : 'Max Payout Date',
      loading           : 'Loading...',
      error             : 'An error occurred while fetching payout information.',
      pleaseLogin       : 'Please log in first.',
      noUpcoming        : 'No upcoming payout scheduled.',
      payMeNow          : 'PayMe Now',
      payNowFail        : 'Unable to request instant payout.',
    },
    ja: {
      pageTitle         : 'マイペイアウト',
      paymentInterval   : '即時利用可能残高',
      paymentDay        : '支払日',
      payoutBalanceSoon : 'まもなく利用可能',
      expectedDateLabel : '最大ペイアウト日',
      loading           : '読み込み中...',
      error             : 'ペイアウト情報の取得中にエラーが発生しました。',
      pleaseLogin       : '最初にログインしてください。',
      noUpcoming        : '次のペイアウトは予定されていません。',
      payMeNow          : '今すぐ入金',
      payNowFail        : '即時ペイアウトをリクエストできませんでした。',
    },
    pt: {
      pageTitle         : 'Meus Pagamentos',
      paymentInterval   : 'Disponível para Saque',
      paymentDay        : 'Dia de Pagamento',
      payoutBalanceSoon : 'Disponível em Breve',
      expectedDateLabel : 'Data Máxima de Pagamento',
      loading           : 'Carregando...',
      error             : 'Ocorreu um erro ao buscar as informações de pagamento.',
      pleaseLogin       : 'Por favor, faça login primeiro.',
      noUpcoming        : 'Nenhum pagamento futuro agendado.',
      payMeNow          : 'PayMe Now',
      payNowFail        : 'Não foi possível solicitar o payout instantâneo.',
    },
  }
  const t = translations[lang] || translations.en

  /* ─────────── Helpers ─────────── */
  function handlePayoutData (data = {}) {
    // interval (for manual-schedule check only)
    if (data.interval) {
      setIntervalVal(
        data.interval.charAt(0).toUpperCase() + data.interval.slice(1)
      )
    }

    /* Expected payout date */
    if (data.expectedPayoutDate) {
      const d = new Date(data.expectedPayoutDate * 1000)
      setExpectedDate(
        d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      )
    } else {
      setExpectedDate(null)
    }

    /* Anchor for Payment Day */
    if (data.weekly_anchor) {
      setAnchor(
        data.weekly_anchor.charAt(0).toUpperCase() +
        data.weekly_anchor.slice(1)
      )
    } else if (data.interval === 'manual') {
      setAnchor(
        expectedDate ? expectedDate : '—'
      )
    } else {
      setAnchor('—')
    }

    /* Balances */
    setBalance  (data.balance   || 0)
    setAvailable(data.available || 0)
  }

  async function fetchPayoutData () {
    if (!token || !user.id) {
      setError(t.pleaseLogin)
      return
    }
    setLoading(true); setError('')
    try {
      const res = await axiosInstance.get('/api/connect/my-payouts')
      handlePayoutData(res.data)
    } catch (e) {
      console.error(e)
      setError(t.error)
    } finally {
      setLoading(false)
    }
  }

  async function handlePayMeNow () {
    if (isPayingNow || available <= 0) return
    setIsPayingNow(true); setError('')
    try {
      const res = await axiosInstance.post('/api/connect/instant-payout', {
        amount: available
      })
      let msg = 'Payout requested!'
      const ts = res.data?.estimatedArrivalDate
      if (ts) {
        const str = new Date(ts * 1000).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric'
        })
        msg = `Payout requested! Funds estimated arrival ${str}`
      }
      setModalOk(true); setModalMsg(msg); setModalOpen(true)
      setTimeout(fetchPayoutData, 4000)
    } catch (e) {
      console.error(e)
      setModalOk(false); setModalMsg(t.payNowFail); setModalOpen(true)
    } finally {
      setIsPayingNow(false)
    }
  }

  useEffect(() => { if (token) fetchPayoutData() }, [token])

  /* ─────────── Guards ─────────── */
  if (!token || !user.id) {
    return (
      <div className="max-w-xl mx-auto p-4 text-red-600">{t.pleaseLogin}</div>
    )
  }

  /* ─────────── UI ─────────── */
  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h2 className="text-2xl font-semibold mb-6">{t.pageTitle}</h2>

      {loading && <p className="text-gray-500">{t.loading}</p>}
      {error   && <p className="text-red-500">{error}</p>}

      {!loading && !error && (
        <>
          {/* Four-card grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* 1. Available for Payout (was Interval) */}
            <InfoCard
              color="green"
              label={t.paymentInterval}
              value={`¥${available}`}
              iconWallet
            />

            {/* 2. Payment Day */}
            <InfoCard
              color="orange"
              label={t.paymentDay}
              value={anchor || '—'}
              iconCalendar
            />

            {/* 3. Available Soon */}
            <InfoCard
              color="sky"
              label={t.payoutBalanceSoon}
              value={`¥${balance}`}
              iconWallet
            />

            {/* 4. Max Payout Date */}
            <InfoCard
              color="purple"
              label={t.expectedDateLabel}
              value={expectedDate || t.noUpcoming}
              iconBell
            />
          </div>

          {/* PayMe Now */}
          {interval.toLowerCase() === 'manual' && available > 0 && (
            <div className="mt-8 flex justify-end">
              <button
                onClick={handlePayMeNow}
                disabled={isPayingNow}
                className={`px-6 py-2 rounded-md text-white font-medium transition-colors ${
                  isPayingNow
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isPayingNow ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12" cy="12" r="10"
                      stroke="currentColor" strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                ) : t.payMeNow}
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6 w-11/12 sm:w-96">
            <h3
              className={`text-lg font-semibold mb-4 ${
                modalOk ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {modalOk ? 'Success' : 'Error'}
            </h3>
            <p className="mb-6 text-gray-700 dark:text-gray-200">{modalMsg}</p>
            <button
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md w-full"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─────────────────────────────────────────────────────────── */
/*   InfoCard dumb component                                  */
/* ─────────────────────────────────────────────────────────── */
function InfoCard ({
  color,
  label,
  value,
  iconPath1,
  iconPath2,
  iconCalendar,
  iconWallet,
  iconBell
}) {
  /* choose icon */
  let innerIcon = null
  if (iconCalendar) {
    innerIcon = (
      <>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8"  y1="2" x2="8"  y2="6" />
        <line x1="3"  y1="10" x2="21" y2="10" />
      </>
    )
  } else if (iconWallet) {
    innerIcon = (
      <>
        <path d="M22 12v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-8Z" />
        <path d="M2 10V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4" />
        <path d="M18 12a2 2 0 1 1 0 4" />
      </>
    )
  } else if (iconBell) {
    innerIcon = (
      <>
        <path d="M18 8a6 6 0 0 0-9.33-5" />
        <path d="M13.29 20a2 2 0 0 1-2.58 0" />
        <path d="M2 13h20" />
        <path d="M8 8v2a4 4 0 0 0 8 0V8" />
        <path d="M8 14v2a4 4 0 0 0 8 0v-2" />
      </>
    )
  } else {
    innerIcon = (
      <>
        <path d={iconPath1} />
        <path d={iconPath2} />
      </>
    )
  }

  /* tailwind dynamic colour strings */
  const bg  = `bg-${color}-100 dark:bg-${color}-500/20`
  const brd = `border-${color}-500/20`
  const txt = `text-${color}-200/50 dark:text-${color}-500/20`
  const dot = `bg-${color}-500 text-${color}-50`

  return (
    <div className={`relative overflow-hidden ${bg} ${brd} card rounded-md shadow-sm p-4`}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`absolute top-0 ltr:-right-10 rtl:-left-10 size-32 ${txt}`}
        fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
      >
        {innerIcon}
      </svg>

      <div className={`flex items-center justify-center w-12 h-12 ${dot} rounded-md text-sm relative z-10`}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20" height="20" fill="none"
          stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        >
          {innerIcon}
        </svg>
      </div>

      <h5 className="mt-3 mb-1 text-lg font-semibold text-gray-800 dark:text-slate-200 relative z-10">
        {label}
      </h5>
      <p className="text-base text-gray-700 dark:text-slate-100 break-words relative z-10">
        {value}
      </p>
    </div>
  )
}

export default MyPayoutsPage
// ────────────────────────────────────────────────────────────────
